<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SettingsLayout from 'dashboard/components-next/Settings/SettingsLayout.vue';
import CalendarCard from './components/CalendarCard.vue';
import AddCalendarModal from './components/AddCalendarModal.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const showAddModal = ref(false);
const uiFlags = computed(() => getters['calendars/getUIFlags'].value || {});
const calendars = computed(() => getters['calendars/getCalendars'].value || []);
const drives = computed(() => getters['drives/getDrives'].value || []);

const isLoading = computed(() => uiFlags.value.isFetching);
const isEmpty = computed(() => calendars.value.length === 0);
const hasDrives = computed(() => drives.value.length > 0);

onMounted(() => {
  store.dispatch('calendars/get');
  store.dispatch('drives/get');
});

const openAddModal = () => {
  if (!hasDrives.value) {
    useAlert(t('INTEGRATION_SETTINGS.CALENDARS.NO_DRIVES_ERROR'));
    return;
  }
  showAddModal.value = true;
};

const closeAddModal = () => {
  showAddModal.value = false;
};

const handleDelete = async calendarId => {
  // eslint-disable-next-line no-alert
  if (window.confirm(t('INTEGRATION_SETTINGS.CALENDARS.DELETE.CONFIRM'))) {
    try {
      await store.dispatch('calendars/delete', calendarId);
      useAlert(t('INTEGRATION_SETTINGS.CALENDARS.DELETE.SUCCESS'));
    } catch (error) {
      useAlert(
        error?.message || t('INTEGRATION_SETTINGS.CALENDARS.DELETE.ERROR')
      );
    }
  }
};

const handleUpdateStatus = async (calendarId, status) => {
  try {
    await store.dispatch('calendars/update', {
      id: calendarId,
      status,
    });
    useAlert(t('INTEGRATION_SETTINGS.CALENDARS.UPDATE.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.message || t('INTEGRATION_SETTINGS.CALENDARS.UPDATE.ERROR')
    );
  }
};
</script>

<template>
  <SettingsLayout
    :header-title="$t('INTEGRATION_SETTINGS.CALENDARS.HEADER')"
    :button-label="$t('INTEGRATION_SETTINGS.CALENDARS.ADD_BUTTON')"
    @click="openAddModal"
  >
    <template #action>
      <AddCalendarModal v-if="showAddModal" @close="closeAddModal" />
    </template>

    <!-- No Drives Warning -->
    <div
      v-if="!hasDrives && !isLoading"
      class="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
    >
      <div class="flex items-start">
        <Icon
          icon="i-lucide-alert-triangle"
          class="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3"
        />
        <div class="flex-1">
          <h4
            class="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1"
          >
            {{ $t('INTEGRATION_SETTINGS.CALENDARS.NO_DRIVES_TITLE') }}
          </h4>
          <p class="text-sm text-yellow-700 dark:text-yellow-300">
            {{ $t('INTEGRATION_SETTINGS.CALENDARS.NO_DRIVES_MESSAGE') }}
          </p>
          <router-link
            :to="`/app/accounts/${getters.getCurrentAccountId.value}/settings/integrations/drives`"
            class="inline-flex items-center mt-2 text-sm font-medium text-yellow-800 dark:text-yellow-200 hover:text-yellow-900 dark:hover:text-yellow-100"
          >
            {{ $t('INTEGRATION_SETTINGS.CALENDARS.GO_TO_DRIVES') }}
            <Icon icon="i-lucide-arrow-right" class="w-4 h-4 ml-1" />
          </router-link>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-12 text-center"
    >
      <div
        class="w-16 h-16 mb-4 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center"
      >
        <Icon icon="i-lucide-calendar" class="w-8 h-8 text-slate-400" />
      </div>
      <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
        {{ $t('INTEGRATION_SETTINGS.CALENDARS.EMPTY_STATE') }}
      </h3>
      <p class="text-slate-600 dark:text-slate-400 mb-6 max-w-md">
        {{ $t('INTEGRATION_SETTINGS.CALENDARS.DESCRIPTION') }}
      </p>
      <Button
        v-if="hasDrives"
        :label="$t('INTEGRATION_SETTINGS.CALENDARS.ADD_FIRST_CALENDAR')"
        icon="i-lucide-plus"
        @click="openAddModal"
      />
    </div>

    <!-- Calendar List -->
    <div v-else class="space-y-4">
      <CalendarCard
        v-for="calendar in calendars"
        :key="calendar.id"
        :calendar="calendar"
        @update-status="handleUpdateStatus"
        @delete="handleDelete"
      />
    </div>
  </SettingsLayout>
</template>
